import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../messages/en-US';
import {
  WORK_ORDERS_LEGEND_STATUS_ORDER,
  WORK_ORDERS_STATUS_DISPLAY_COLORS,
  WORK_ORDERS_WINDOW_BORDER_STROKE,
  WORK_ORDERS_LATE_BORDER_STROKE,
  WORK_ORDERS_LATE_PENDING_COLOR,
  StatusWithinWindow,
} from './WorkOrdersTypes';

function labelForStatus(
  localizationService: ReturnType<typeof useLocalization>,
  status: StatusWithinWindow
): string {
  switch (status) {
    case 'compliant':
      return localizationService.toLanguageString('custom.compliant', enMessages.custom.compliant);
    case 'partial':
      return localizationService.toLanguageString(
        'custom.partiallyCompliant',
        enMessages.custom.partiallyCompliant
      );
    case 'nonCompliant':
      return localizationService.toLanguageString('custom.nonCompliant', enMessages.custom.nonCompliant);
    case 'pending':
      return localizationService.toLanguageString('custom.pending', enMessages.custom.pending);
    default:
      return status;
  }
}

export default function WorkOrdersLegend({ className = '' }: { className?: string }) {
  const localizationService = useLocalization();

  const activeScheduleLabel = localizationService.toLanguageString(
    'custom.workOrdersLegendActiveSchedule',
    enMessages.custom.workOrdersLegendActiveSchedule
  );
  const expiredScheduleLabel = localizationService.toLanguageString(
    'custom.workOrdersLegendExpiredSchedule',
    enMessages.custom.workOrdersLegendExpiredSchedule
  );

  return (
    <div className={`min-w-0 ${className}`}>
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-700"
        role="group"
        aria-label={localizationService.toLanguageString(
          'custom.workOrdersLegendAria',
          enMessages.custom.workOrdersLegendAria
        )}
      >
        {WORK_ORDERS_LEGEND_STATUS_ORDER.map((status) => {
          const { fill, stroke } = WORK_ORDERS_STATUS_DISPLAY_COLORS[status];
          return (
            <div key={status} className="flex shrink-0 items-center gap-2">
              <span
                className="inline-block h-3.5 w-6 shrink-0 rounded-sm border"
                style={{ backgroundColor: fill, borderColor: stroke }}
              />
              <span>{labelForStatus(localizationService, status)}</span>
            </div>
          );
        })}
        <div className="flex shrink-0 items-center gap-2">
          <span
            className="inline-block h-3.5 w-8 shrink-0 rounded-sm border-2 bg-transparent"
            style={{ borderColor: WORK_ORDERS_WINDOW_BORDER_STROKE.active }}
          />
          <span>{activeScheduleLabel}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className="inline-block h-3.5 w-8 shrink-0 rounded-sm border-2 bg-transparent"
            style={{ borderColor: WORK_ORDERS_WINDOW_BORDER_STROKE.inactive }}
          />
          <span>{expiredScheduleLabel}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className="inline-block h-3.5 w-6 shrink-0 rounded-sm border"
            style={{ backgroundColor: WORK_ORDERS_LATE_PENDING_COLOR.fill, borderColor: WORK_ORDERS_LATE_PENDING_COLOR.stroke }}
          />
          <span>
            {localizationService.toLanguageString('custom.workOrdersLegendLatePending', 'Late Pending')}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className="inline-block h-3.5 w-8 shrink-0 rounded-sm border-2 border-dashed bg-transparent"
            style={{ borderColor: WORK_ORDERS_LATE_BORDER_STROKE }}
          />
          <span>
            {localizationService.toLanguageString('custom.workOrdersLegendLate', 'Late')}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className="inline-block h-4 w-4 shrink-0 rounded-full border-2"
            style={{ borderColor: '#dc2626', backgroundColor: '#fecaca' }}
          />
          <span>
            {localizationService.toLanguageString(
              'custom.workOrdersLegendUnscheduled',
              enMessages.custom.workOrdersLegendUnscheduled
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
